import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Pencil,
  ImagePlus,
  Menu,
  PackagePlus,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  Video,
  X,
  Camera,
  Printer,
  QrCode,
} from "lucide-react";

import { BrowserMultiFormatReader } from "@zxing/browser";
import { QRCodeCanvas } from "qrcode.react";

import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";
import deviceCatalog from "../../data/deviceCatalog.json";

const CATEGORY_OPTIONS = [
  "Telefon",
  "Tablet",
  "Laptop",
  "Smartwatch",
  "Kufje",
  "Aksesor",
  "Tjetër",
];

// Device catalog helpers: suggestions remain editable for custom devices.
const CATALOG_CATEGORIES = deviceCatalog?.categories || {};
const getCatalogBrands = (category) => Object.keys(CATALOG_CATEGORIES[category] || {});
const getCatalogModels = (category, brand) => CATALOG_CATEGORIES[category]?.[brand] || [];
const getCatalogEntry = (category, brand, model) =>
  getCatalogModels(category, brand).find(
    (item) => item.model.toLocaleLowerCase() === String(model || "").toLocaleLowerCase()
  );
const uniqueCatalogValues = (values) => [...new Set(values.filter(Boolean))];

// Custom dropdown entries are retained on this browser for future purchases.
const CUSTOM_CATALOG_KEY = "masterphone-device-custom-catalog-v1";
const readCustomCatalog = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(CUSTOM_CATALOG_KEY) || "{}");
    return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  } catch {
    return {};
  }
};
const catalogScope = (name, values) =>
  [name, ...values].map((value) => String(value || "").trim().toLocaleLowerCase("sq")).join("::");
const mergeOptions = (...groups) =>
  [...new Map(groups.flat().filter(Boolean).map((value) => [String(value).trim().toLocaleLowerCase("sq"), String(value).trim()])).values()];

const CONDITION_OPTIONS = [
  "Si i ri",
  "Shumë i mirë",
  "I mirë",
  "I përdorur",
  "Me shenja përdorimi",
  "Me defekt",
];

const YES_NO_OPTIONS = ["Jo", "Po"];

const SERVICED_PART_OPTIONS = [
  "Kamerë",
  "Bateri",
  "Ekran",
  "Xham para /prapa",
  "Të tjera",
];

const EMPTY_FORM = {
  sourceType: "customer",
  supplierId: "",
  purchaseDate: new Date().toISOString().slice(0, 10),
  invoiceNumber: "",
  amountPaid: "0",

  customerName: "",
  customerSurname: "",
  customerPhone: "",
  customerIdCard: "",

  category: "Telefon",
  subcategory: "",
  brand: "",
  model: "",
  storage: "",
  ram: "",
  color: "",
  imei: "",
  serialNumber: "",
  condition: "I përdorur",
  batteryPercentage: "",
  serviced: "Jo",
  servicedParts: [],
  description: "",
  defects: "",

  purchasePrice: "",
  salePrice: "",

  images: [],
  videos: [],
};

const formatMoney = (value) =>
  new Intl.NumberFormat("sq-AL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const normalizeUploads = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getMediaUrl = (value) => {
  if (!value) return "";

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${API_BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
};

const AdminDevicePurchases = () => {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerLoadError, setCustomerLoadError] = useState("");
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [selectedCustomerKey, setSelectedCustomerKey] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [detailPurchase, setDetailPurchase] = useState(null);

  const qrVideoRef = useRef(null);
  const qrScannerControlsRef = useRef(null);
  const qrPrintRef = useRef(null);

  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrScannerBusy, setQrScannerBusy] = useState(false);
  const [qrScannerError, setQrScannerError] = useState("");

  const [qrPurchase, setQrPurchase] = useState(null);
  const [qrValue, setQrValue] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [search, setSearch] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  // Mesazhet e formularit shfaqen BRENDA modalit.
  const [modalMessage, setModalMessage] = useState({
    type: "",
    text: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const [customCatalog, setCustomCatalog] = useState(readCustomCatalog);
  const catalogOptionsFor = useCallback((name, scope, defaults = []) =>
    mergeOptions(defaults, customCatalog[catalogScope(name, scope)] || []), [customCatalog]);
  const addCatalogOption = useCallback((name, scope, rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return;
    const key = catalogScope(name, scope);
    setCustomCatalog((previous) => {
      const next = { ...previous, [key]: mergeOptions(previous[key] || [], [value]) };
      try { localStorage.setItem(CUSTOM_CATALOG_KEY, JSON.stringify(next)); }
      catch (error) { console.warn("Custom catalog could not be saved:", error); }
      return next;
    });
  }, []);
  const catalogBrands = catalogOptionsFor("brand", [formData.category], getCatalogBrands(formData.category));
  const catalogModels = getCatalogModels(formData.category, formData.brand);
  const selectedCatalogEntry = getCatalogEntry(formData.category, formData.brand, formData.model);
  const catalogOptions = {
    storage: catalogOptionsFor("storage", [formData.category, formData.brand, formData.model], selectedCatalogEntry?.storageOptions || []),
    ram: catalogOptionsFor("ram", [formData.category, formData.brand, formData.model], selectedCatalogEntry?.ramOptions || []),
    color: catalogOptionsFor("color", [formData.category, formData.brand, formData.model], selectedCatalogEntry?.colorOptions || []),
  };

  const potentialProfit = useMemo(
    () =>
      Number(formData.salePrice || 0) -
      Number(formData.purchasePrice || 0),
    [formData.purchasePrice, formData.salePrice]
  );

  const remainingDebt = useMemo(
    () =>
      Math.max(
        Number(formData.purchasePrice || 0) -
          Number(formData.amountPaid || 0),
        0
      ),
    [formData.purchasePrice, formData.amountPaid]
  );

  const getToken = () =>
    localStorage.getItem("authToken") || localStorage.getItem("token");

  const fetchPurchases = useCallback(async () => {
    const token = getToken();

    if (!token) {
      navigate("/login");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/device-purchases`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const text = await response.text();
      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { error: text };
      }

      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.sqlMessage ||
            data?.error ||
            data?.message ||
            "Blerjet nuk mund të ngarkoheshin."
        );
      }

      const rows = Array.isArray(data)
        ? data
        : data?.purchases || data?.data || [];

      setPurchases(rows);
    } catch (error) {
      console.error("FETCH DEVICE PURCHASES ERROR:", error);

      setMessage({
        type: "error",
        text: error?.message || "Blerjet nuk mund të ngarkoheshin.",
      });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const fetchSuppliers = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;

      const data = await response.json();
      setSuppliers(
        (Array.isArray(data) ? data : data?.suppliers || []).filter(
          (supplier) => Number(supplier.is_active) !== 0
        )
      );
    } catch (error) {
      console.error("FETCH SUPPLIERS ERROR:", error);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const filteredPurchases = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return purchases;

    return purchases.filter((purchase) =>
      [
        purchase.customer_name,
        purchase.customer_surname,
        purchase.supplier_name,
        purchase.customer_phone,
        purchase.customer_id_card,
        purchase.category,
        purchase.brand,
        purchase.model,
        purchase.imei,
        purchase.serial_number,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(value)
    );
  }, [purchases, search]);

  // Fetch the full customer directory independently of the purchases list.
  const fetchCustomers = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      setCustomerLoadError("");
      const response = await fetch(`${API_BASE_URL}/api/device-purchases/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Customer directory unavailable");
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("FETCH CUSTOMERS ERROR:", error);
      setCustomerLoadError("Lista e klientëve nuk u ngarkua. Kontrollo API-n.");
    }
  }, []);

  useEffect(() => {
    if (dialogOpen && formData.sourceType === "customer") fetchCustomers();
  }, [dialogOpen, formData.sourceType, fetchCustomers]);

  const filteredCustomers = useMemo(() => {
    const normalize = (value) => String(value ?? "").trim().toLocaleLowerCase("sq");
    const query = normalize(formData.customerName);
    if (!query) return [];

    return customers
      .filter((customer) =>
        normalize(`${customer.name} ${customer.surname}`).includes(query)
      )
      .sort((a, b) => {
        const aStarts = normalize(a.name).startsWith(query);
        const bStarts = normalize(b.name).startsWith(query);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        return `${a.name} ${a.surname}`.localeCompare(
          `${b.name} ${b.surname}`, "sq"
        );
      });
  }, [customers, formData.customerName]);

  const selectCustomer = (customer) => {
    setFormData((previous) => ({
      ...previous,
      customerName: customer.name,
      customerSurname: customer.surname,
      customerPhone: customer.phone,
      customerIdCard: customer.idCard,
    }));
    setSelectedCustomerKey(customer.key);
    setShowCustomerSuggestions(false);
    setFieldErrors((previous) => {
      const next = { ...previous };
      for (const field of ["customerName", "customerSurname", "customerPhone", "customerIdCard"]) {
        delete next[field];
      }
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "customerName") {
      setSelectedCustomerKey(null);
      setShowCustomerSuggestions(true);
    }
    if (editingPurchase && ["sourceType", "supplierId", "amountPaid"].includes(name)) return;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "category" ? { brand: "", model: "", storage: "", ram: "", color: "" } : {}),
      ...(name === "brand" ? { model: "", storage: "", ram: "", color: "" } : {}),
      ...(name === "model" ? { storage: "", ram: "", color: "" } : {}),
      ...(name === "serviced" && value === "Jo" ? { servicedParts: [] } : {}),
    }));

    // Fshi mesazhin e gabimit sapo përdoruesi fillon korrigjimin.
    setModalMessage((previous) =>
      previous.type === "error"
        ? { type: "", text: "" }
        : previous
    );

    setFieldErrors((previous) => {
      if (!previous[name]) return previous;

      const next = { ...previous };
      delete next[name];
      return next;
    });
  };

  const openNewPurchase = () => {
    setSelectedCustomerKey(null);
    setShowCustomerSuggestions(false);
    setEditingPurchase(null);
    setFormData({ ...EMPTY_FORM, purchaseDate: new Date().toISOString().slice(0, 10) });
    setFieldErrors({});
    setModalMessage({ type: "", text: "" });
    setDialogOpen(true);
  };

  // Existing media are retained explicitly; only new File objects are uploaded.
  // PUT backend must validate retained paths against media owned by this purchase.
  const openEditPurchase = (purchase) => {
    setSelectedCustomerKey(null);
    setShowCustomerSuggestions(false);
    if (saving || deletingId === purchase.id) return;
    revokePreviews(formData.images);
    revokePreviews(formData.videos);
    setEditingPurchase(purchase);
    setFormData({
      ...EMPTY_FORM,
      sourceType: purchase.supplier_id ? "supplier" : "customer",
      supplierId: String(purchase.supplier_id ?? ""),
      purchaseDate: String(purchase.purchase_date || purchase.created_at || "").slice(0, 10),
      customerName: String(purchase.customer_name ?? EMPTY_FORM.customerName),
      customerSurname: String(purchase.customer_surname ?? EMPTY_FORM.customerSurname),
      customerPhone: String(purchase.customer_phone ?? EMPTY_FORM.customerPhone),
      customerIdCard: String(purchase.customer_id_card ?? EMPTY_FORM.customerIdCard),
      category: String(purchase.category ?? EMPTY_FORM.category),
      subcategory: String(purchase.subcategory ?? EMPTY_FORM.subcategory),
      brand: String(purchase.brand ?? EMPTY_FORM.brand),
      model: String(purchase.model ?? EMPTY_FORM.model),
      storage: String(purchase.storage ?? EMPTY_FORM.storage),
      ram: String(purchase.ram ?? EMPTY_FORM.ram),
      color: String(purchase.color ?? EMPTY_FORM.color),
      imei: String(purchase.imei ?? EMPTY_FORM.imei),
      serialNumber: String(purchase.serial_number ?? EMPTY_FORM.serialNumber),
      condition: String(purchase.device_condition ?? purchase.condition ?? EMPTY_FORM.condition),
      batteryPercentage: String(purchase.battery_percentage ?? EMPTY_FORM.batteryPercentage),
      description: String(purchase.description ?? EMPTY_FORM.description),
      defects: String(purchase.defects ?? EMPTY_FORM.defects),
      purchasePrice: String(purchase.purchase_price ?? EMPTY_FORM.purchasePrice),
      salePrice: String(purchase.sale_price ?? EMPTY_FORM.salePrice),
      invoiceNumber: String(purchase.invoice_number ?? EMPTY_FORM.invoiceNumber),
      amountPaid: String(purchase.amount_paid ?? EMPTY_FORM.amountPaid),
      serviced: String(purchase.serviced || "").toLowerCase() === "po" ? "Po" : "Jo",
      servicedParts: normalizeUploads(purchase.serviced_parts).length ? normalizeUploads(purchase.serviced_parts) : purchase.serviced_parts ? [String(purchase.serviced_parts)] : [],
      images: normalizeUploads(purchase.images).map((path) => ({ path, preview: getMediaUrl(path) })),
      videos: normalizeUploads(purchase.videos).map((path) => ({ path, preview: getMediaUrl(path) })),
    });
    setFieldErrors({});
    setModalMessage({ type: "", text: "" });
    setDetailPurchase(null);
    setDialogOpen(true);
  };

  const revokePreviews = (items = []) => {
    items.forEach((item) => {
      if (item?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
    });
  };

  const closeDialog = () => {
    setSelectedCustomerKey(null);
    setShowCustomerSuggestions(false);
    if (saving) return;

    revokePreviews(formData.images);
    revokePreviews(formData.videos);

    setDialogOpen(false);
    setEditingPurchase(null);
    setFormData(EMPTY_FORM);
    setFieldErrors({});
    setModalMessage({ type: "", text: "" });
  };

  const handleImages = (event) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      file.type.startsWith("image/")
    );

    if (!files.length) return;

    setFormData((previous) => ({
      ...previous,
      images: [
        ...previous.images,
        ...files.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        })),
      ],
    }));

    event.target.value = "";
  };

  const handleVideos = (event) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      file.type.startsWith("video/")
    );

    if (!files.length) return;

    setFormData((previous) => ({
      ...previous,
      videos: [
        ...previous.videos,
        ...files.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        })),
      ],
    }));

    event.target.value = "";
  };

  const removeImage = (index) => {
    setFormData((previous) => {
      const item = previous.images[index];

      if (item?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }

      return {
        ...previous,
        images: previous.images.filter((_, i) => i !== index),
      };
    });
  };

  const removeVideo = (index) => {
    setFormData((previous) => {
      const item = previous.videos[index];

      if (item?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }

      return {
        ...previous,
        videos: previous.videos.filter((_, i) => i !== index),
      };
    });
  };

  const validateForm = () => {
    const errors = {};

    if (formData.sourceType === "supplier" && !formData.supplierId) {
      errors.supplierId = "Zgjidh furnitorin.";
    }

    if (formData.sourceType === "customer" && !formData.customerName.trim()) {
      errors.customerName = "Vendos emrin e klientit.";
    }

    if (formData.sourceType === "customer" && !formData.customerSurname.trim()) {
      errors.customerSurname = "Vendos mbiemrin e klientit.";
    }

    if (formData.sourceType === "customer" && !formData.customerPhone.trim()) {
      errors.customerPhone = "Vendos numrin e telefonit.";
    }

    if (formData.sourceType === "customer" && !formData.customerIdCard.trim()) {
      errors.customerIdCard = "Vendos numrin e Kartës ID.";
    }

    if (!formData.category.trim()) {
      errors.category = "Zgjidh kategorinë.";
    }

    if (!formData.brand.trim()) {
      errors.brand = "Vendos brand-in.";
    }

    if (!formData.model.trim()) {
      errors.model = "Vendos modelin.";
    }

    if (
      formData.batteryPercentage !== "" &&
      (!Number.isFinite(Number(formData.batteryPercentage)) ||
        Number(formData.batteryPercentage) < 0 ||
        Number(formData.batteryPercentage) > 100)
    ) {
      errors.batteryPercentage = "Bateria duhet të jetë nga 0 deri në 100%.";
    }

    if (formData.serviced === "Po" && !formData.servicedParts.length) {
      errors.servicedParts = "Zgjidh të paktën një pjesë të servisuar.";
    }

    if (
      formData.purchasePrice === "" ||
      !Number.isFinite(Number(formData.purchasePrice)) ||
      Number(formData.purchasePrice) < 0
    ) {
      errors.purchasePrice = "Vendos një çmim blerjeje të vlefshëm.";
    }

    if (
      formData.salePrice === "" ||
      !Number.isFinite(Number(formData.salePrice)) ||
      Number(formData.salePrice) < 0
    ) {
      errors.salePrice = "Vendos një çmim shitjeje të vlefshëm.";
    }

    if (
      formData.sourceType === "supplier" &&
      (!Number.isFinite(Number(formData.amountPaid)) ||
        Number(formData.amountPaid) < 0 ||
        Number(formData.amountPaid) > Number(formData.purchasePrice || 0))
    ) {
      errors.amountPaid = "Pagesa nuk mund të jetë më e madhe se blerja.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    if (!validateForm()) {
      setModalMessage({
        type: "error",
        text: "Kontrollo fushat e detyrueshme të shënuara me të kuqe.",
      });

      window.setTimeout(() => {
        document
          .querySelector("[data-purchase-form-scroll]")
          ?.scrollTo({ top: 0, behavior: "smooth" });
      }, 0);

      return;
    }

    const token = getToken();

    if (!token) {
      navigate("/login");
      return;
    }

    setSaving(true);
    setModalMessage({ type: "", text: "" });

    try {
      const body = new FormData();

      body.append("sourceType", formData.sourceType);
      body.append("supplierId", formData.sourceType === "supplier" ? formData.supplierId : "");
      body.append("purchaseDate", formData.purchaseDate);
      body.append("invoiceNumber", formData.invoiceNumber.trim());
      body.append("amountPaid", formData.sourceType === "supplier" ? String(Number(formData.amountPaid || 0)) : String(Number(formData.purchasePrice || 0)));

      body.append("customerName", formData.sourceType === "customer" ? formData.customerName.trim() : "");
      body.append("customerSurname", formData.sourceType === "customer" ? formData.customerSurname.trim() : "");
      body.append("customerPhone", formData.sourceType === "customer" ? formData.customerPhone.trim() : "");
      body.append("customerIdCard", formData.sourceType === "customer" ? formData.customerIdCard.trim() : "");

      body.append("category", formData.category.trim());
      body.append("subcategory", formData.subcategory.trim());
      body.append("brand", formData.brand.trim());
      body.append("model", formData.model.trim());
      body.append("storage", formData.storage.trim());
      body.append("ram", formData.ram.trim());
      body.append("color", formData.color.trim());
      body.append("imei", formData.imei.trim());
      body.append("serialNumber", formData.serialNumber.trim());
      body.append("condition", formData.condition.trim());
      body.append("batteryPercentage", formData.batteryPercentage === "" ? "" : String(Number(formData.batteryPercentage)));
      body.append("serviced", formData.serviced);
      body.append("servicedParts", JSON.stringify(formData.servicedParts));
      body.append("description", formData.description.trim());
      body.append("defects", formData.defects.trim());

      body.append("purchasePrice", String(Number(formData.purchasePrice)));
      body.append("salePrice", String(Number(formData.salePrice)));

      formData.images.forEach((item) => {
        if (item.file) body.append("images", item.file);
      });

      formData.videos.forEach((item) => {
        if (item.file) body.append("videos", item.file);
      });

      if (editingPurchase) {
        body.append("existingImages", JSON.stringify(formData.images.filter((item) => !item.file).map((item) => item.path)));
        body.append("existingVideos", JSON.stringify(formData.videos.filter((item) => !item.file).map((item) => item.path)));
      }

      const response = await fetch(
        editingPurchase
          ? `${API_BASE_URL}/api/device-purchases/${encodeURIComponent(editingPurchase.id)}`
          : `${API_BASE_URL}/api/device-purchases`, {
        method: editingPurchase ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      const text = await response.text();
      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { error: text };
      }

      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.sqlMessage ||
            data?.error ||
            data?.message ||
            "Blerja nuk mund të regjistrohej."
        );
      }

      const rawSavedPurchase = data?.purchase || data?.data || data;
      const selectedSupplier = suppliers.find(
        (supplier) => String(supplier.id) === String(formData.supplierId)
      );
      const savedPurchase =
        formData.sourceType === "supplier"
          ? {
              ...rawSavedPurchase,
              supplier_id:
                rawSavedPurchase.supplier_id || formData.supplierId,
              supplier_name:
                rawSavedPurchase.supplier_name || selectedSupplier?.name,
              amount_paid:
                rawSavedPurchase.amount_paid ?? Number(formData.amountPaid || 0),
              amount_due:
                rawSavedPurchase.amount_due ?? remainingDebt,
              invoice_number:
                rawSavedPurchase.invoice_number || formData.invoiceNumber,
            }
          : rawSavedPurchase;

      if (editingPurchase) {
        // Refresh authoritative rows; the update response may contain only a message.
        await fetchPurchases();
      } else {
        await fetchPurchases();
      }

       const generatedBarcode =
      data?.barcode ||
      rawSavedPurchase?.barcode ||
      rawSavedPurchase?.product_barcode ||
      "";

    if (!editingPurchase && generatedBarcode) {
      setQrPurchase({
        ...savedPurchase,
        barcode: generatedBarcode,
        product_id: data?.product_id || rawSavedPurchase?.product_id || null,
      });
      setQrValue(generatedBarcode);
    }
      

      // SUCCESS:
      // 1. Pastro preview-t
      // 2. Mbyll modalin
      // 3. Pastro formularin
      // 4. Shfaq suksesin në faqen kryesore
      revokePreviews(formData.images);
      revokePreviews(formData.videos);

      setDialogOpen(false);
      setEditingPurchase(null);
      setFormData(EMPTY_FORM);
      setFieldErrors({});
      setModalMessage({ type: "", text: "" });

      setMessage({
        type: "success",
        text:
          editingPurchase
            ? "Blerja u përditësua me sukses."
            : formData.sourceType === "supplier"
            ? `Blerja u regjistrua. Detyrimi ${formatMoney(remainingDebt)} ALL iu shtua furnitorit.`
            : data?.product_id
              ? `Blerja u regjistrua dhe produkti #${data.product_id} u krijua automatikisht.`
              : "Blerja u regjistrua me sukses.",
      });
    } catch (error) {
      console.error("SAVE DEVICE PURCHASE ERROR:", error);

      // ERROR:
      // Modali qëndron i hapur dhe gabimi shfaqet brenda tij.
      setModalMessage({
        type: "error",
        text: error?.message || (editingPurchase ? "Blerja nuk mund të përditësohej." : "Blerja nuk mund të regjistrohej."),
      });

      window.setTimeout(() => {
        document
          .querySelector("[data-purchase-form-scroll]")
          ?.scrollTo({ top: 0, behavior: "smooth" });
      }, 0);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (purchase) => {
    const confirmed = window.confirm(
      `A dëshiron ta fshish blerjen ${purchase.brand || ""} ${
        purchase.model || ""
      }?`
    );

    if (!confirmed) return;

    const token = getToken();

    if (!token) {
      navigate("/login");
      return;
    }

    setDeletingId(purchase.id);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/device-purchases/${purchase.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.error ||
            data?.message ||
            "Blerja nuk mund të fshihej."
        );
      }

      setPurchases((previous) =>
        previous.filter(
          (item) => Number(item.id) !== Number(purchase.id)
        )
      );

      setMessage({
        type: "success",
        text:
          data?.product_deleted === true
            ? "Blerja dhe produkti i lidhur u fshinë me sukses."
            : "Blerja u fshi me sukses.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "Blerja nuk mund të fshihej.",
      });
    } finally {
      setDeletingId(null);
    }
  };


 const openPurchaseQr = (purchase) => {
  const barcode = purchase?.barcode || purchase?.product_barcode || "";

  if (!barcode) {
    setMessage({
      type: "error",
      text: "Kjo pajisje nuk ka QR/barcode të gjeneruar.",
    });
    return;
  }

  setQrPurchase(purchase);
  setQrValue(barcode);
};

const closePurchaseQr = () => {
  setQrPurchase(null);
  setQrValue("");
};

const printPurchaseQr = () => {
  if (!qrPrintRef.current || !qrValue) return;

  const canvas = qrPrintRef.current.querySelector("canvas");
  if (!canvas) return;

  const qrImage = canvas.toDataURL("image/png");
  const printWindow = window.open("", "_blank", "width=500,height=500");

  if (!printWindow) {
    setMessage({
      type: "error",
      text: "Browseri bllokoi dritaren e printimit. Lejo pop-up dhe provo përsëri.",
    });
    return;
  }

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>Printo QR</title>
      <style>
        @page {
          size: 30mm 30mm;
          margin: 0;
        }

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 30mm;
          height: 30mm;
          overflow: hidden;
        }

        body {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        img {
          display: block;
          width: 25mm;
          height: 25mm;
        }
      </style>
      </head>
      <body>
        <img src="${qrImage}" alt="QR" />
        <script>
          window.onload = function () {
            window.focus();
            window.print();
            window.onafterprint = function () { window.close(); };
          };
        <\/script>
      </body>
    </html>
  `);

  printWindow.document.close();
};



  const lookupPurchaseByQr = async (rawCode) => {
  const barcode = String(rawCode || "").trim();

  if (!barcode) return null;

  const token = getToken();

  if (!token) {
    navigate("/login");
    return null;
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/device-purchases/by-barcode/${encodeURIComponent(
        barcode
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.details ||
          data?.error ||
          "Pajisja nuk u gjet."
      );
    }

    const purchase = data.purchase;

    setDetailPurchase(purchase);

    setMessage({
      type: "success",
      text: `U gjet pajisja ${purchase.brand || ""} ${
        purchase.model || ""
      }.`,
    });

    return purchase;
  } catch (error) {
    setMessage({
      type: "error",
      text:
        error.message ||
        "Pajisja nuk u gjet me këtë QR.",
    });

    return null;
  }
};

const stopQrScanner = () => {
  try {
    qrScannerControlsRef.current?.stop();
  } catch (error) {
    console.warn("QR SCANNER STOP:", error);
  }

  qrScannerControlsRef.current = null;

  const video = qrVideoRef.current;
  const stream = video?.srcObject;

  if (stream?.getTracks) {
    stream.getTracks().forEach((track) => track.stop());
  }

  if (video) {
    video.srcObject = null;
  }

  setQrScannerBusy(false);
};

const closeQrScanner = () => {
  stopQrScanner();
  setQrScannerOpen(false);
  setQrScannerError("");
};


const startQrScanner = () => {
  stopQrScanner();

  setQrScannerError("");
  setQrScannerBusy(true);
  setQrScannerOpen(true);
};


useEffect(() => {
  if (!qrScannerOpen) return;

  let cancelled = false;

  const startCamera = async () => {
    // Presim që modali dhe <video> të montohen në DOM
    await new Promise((resolve) =>
      requestAnimationFrame(() =>
        requestAnimationFrame(resolve)
      )
    );

    if (cancelled) return;

    if (!qrVideoRef.current) {
      setQrScannerBusy(false);
      setQrScannerError(
        "Video nuk është gati. Mbylle dhe provo përsëri."
      );
      return;
    }

    if (!window.isSecureContext) {
      setQrScannerBusy(false);
      setQrScannerError(
        "Kamera kërkon HTTPS. Hape aplikacionin me HTTPS."
      );
      return;
    }

    try {
      const reader = new BrowserMultiFormatReader();

      let handled = false;

      const controls = await reader.decodeFromConstraints(
        {
          video: {
            facingMode: {
              ideal: "environment",
            },
            width: {
              ideal: 1280,
            },
            height: {
              ideal: 720,
            },
          },
          audio: false,
        },
        qrVideoRef.current,
        async (result) => {
          if (!result || handled || cancelled) return;

          const code = String(
            result.getText?.() || result
          ).trim();

          if (!code) return;

          handled = true;

          stopQrScanner();
          setQrScannerOpen(false);

          await lookupPurchaseByQr(code);
        }
      );

      if (cancelled) {
        controls?.stop();
        return;
      }

      qrScannerControlsRef.current = controls;
      setQrScannerBusy(false);
    } catch (error) {
      console.error("QR SCANNER ERROR:", error);

      stopQrScanner();

      let errorMessage = "Kamera nuk mund të hapej.";

      if (error?.name === "NotAllowedError") {
        errorMessage =
          "Nuk është dhënë leje për kamerën. Lejo Camera nga browseri dhe provo përsëri.";
      } else if (error?.name === "NotFoundError") {
        errorMessage = "Nuk u gjet kamera në këtë pajisje.";
      } else if (error?.name === "NotReadableError") {
        errorMessage =
          "Kamera është duke u përdorur nga një aplikacion tjetër.";
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setQrScannerError(errorMessage);
      setQrScannerBusy(false);
    }
  };

  startCamera();

  return () => {
    cancelled = true;
  };
}, [qrScannerOpen]);


useEffect(() => {
  return () => {
    stopQrScanner();
  };
}, []);

  return (
    <div className="dp-design min-h-screen overflow-x-hidden bg-gray-100 text-gray-800">
      <style>{devicePurchasesDesign}</style>
      <style>{purchaseDetailStyles}</style>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="fixed left-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700 text-white shadow-lg lg:hidden"
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>

      <main className="dp-content min-h-screen min-w-0 w-full overflow-x-hidden px-3 pb-10 pt-20 sm:px-5 lg:ml-64 lg:w-[calc(100%-16rem)] lg:px-8 lg:pt-8">
        <div className="dp-title mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Inventari
            </span>

            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              Blerje 
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              Regjistro pajisjet e blera nga klientët ose furnitorët. Detyrimi
              i papaguar i shtohet furnitorit përkatës.
            </p>
          </div>

        <div className="flex flex-col gap-2 sm:flex-row">

        <button
          type="button"
          onClick={startQrScanner}
          className="flex items-center justify-center gap-2 rounded-xl border border-blue-600 bg-white px-5 py-3 font-semibold text-blue-600 shadow-sm transition hover:bg-blue-50"
        >
          <Camera size={19} />
          Skano QR
        </button>

        <button
          type="button"
          onClick={openNewPurchase}
          className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-md transition hover:bg-blue-700"
        >
          <Plus size={19} />
          Blerje e re
        </button>

        </div>
        </div>

        {message.text && (
          <div
            className={`mb-5 rounded-xl border px-4 py-3 text-sm font-medium ${
              message.type === "success"
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-red-200 bg-red-50 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}

        <section className="dp-search-panel mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Kërko klient, furnitor, brand, model ose IMEI..."
              className="w-full rounded-xl border border-gray-300 py-3 pl-10 pr-4 outline-none focus:border-blue-500"
            />
          </div>
        </section>

        <section className="dp-table-panel hidden overflow-hidden rounded-2xl bg-white shadow-sm md:block">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-sm">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left">Pajisja</th>
                  <th className="p-3 text-left">Burimi</th>
                  <th className="p-3 text-left">Kartë ID</th>
                  <th className="p-3 text-left">IMEI / Serial</th>
                  <th className="p-3 text-left">Gjendja</th>
                  <th className="p-3 text-right">Blerja</th>
                  <th className="p-3 text-right">Shitja</th>
                  <th className="p-3 text-right">Fitimi</th>
                  <th className="p-3 text-center">Veprime</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-16 text-center text-gray-500"
                    >
                      Duke ngarkuar...
                    </td>
                  </tr>
                ) : filteredPurchases.length ? (
                  filteredPurchases.map((purchase) => {
                    const profit =
                      Number(purchase.sale_price || 0) -
                      Number(purchase.purchase_price || 0);

                    return (
                      <tr
                        key={purchase.id}
                        tabIndex={0}
                        aria-label={`Shiko blerjen ${purchase.brand || ""} ${purchase.model || ""}`}
                        onClick={(event) => {
                          if (event.target.closest("button, a, input, select, textarea")) return;
                          setDetailPurchase(purchase);
                        }}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setDetailPurchase(purchase);
                          }
                        }}
                        className="cursor-pointer border-b border-gray-100 hover:bg-blue-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                      >
                        <td className="p-3">
                          <div className="font-bold">
                            {purchase.brand} {purchase.model}
                          </div>
                          <div className="mt-1 text-xs text-gray-400">
                            {purchase.category}
                          </div>
                        </td>

                        <td className="p-3">
                          <div className="font-semibold">
                            {purchase.supplier_name ||
                              `${purchase.customer_name || ""} ${purchase.customer_surname || ""}`.trim() ||
                              "—"}
                          </div>
                          <div className="text-xs text-gray-400">
                            {purchase.supplier_id
                              ? "Furnitor"
                              : purchase.customer_phone || "Klient"}
                          </div>
                        </td>

                        <td className="p-3 font-mono text-xs">
                          {purchase.customer_id_card || "—"}
                        </td>

                        <td className="p-3 font-mono text-xs">
                          {purchase.imei ||
                            purchase.serial_number ||
                            "—"}
                        </td>

                        <td className="p-3">
                          {purchase.device_condition || "—"}
                        </td>

                        <td className="p-3 text-right font-bold">
                          {formatMoney(purchase.purchase_price)} ALL
                        </td>

                        <td className="p-3 text-right font-bold text-blue-600">
                          {formatMoney(purchase.sale_price)} ALL
                        </td>

                        <td
                          className={`p-3 text-right font-extrabold ${
                            profit >= 0
                              ? "text-emerald-600"
                              : "text-red-600"
                          }`}
                        >
                          {formatMoney(profit)} ALL
                        </td>

                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => setDetailPurchase(purchase)}
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                              title="Shiko"
                            >
                              <Eye size={17} />
                            </button>

                            <button type="button" onClick={() => openEditPurchase(purchase)}
                              disabled={saving || deletingId === purchase.id}
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              title="Ndrysho" aria-label="Ndrysho blerjen">
                              <Pencil size={17} />
                            </button>

                             <button
                                type="button"
                                onClick={() => openPurchaseQr(purchase)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100"
                                title="Shiko / Printo QR"
                              >
                                <QrCode size={17} />
                              </button>

                            <button
                              type="button"
                              disabled={deletingId === purchase.id}
                              onClick={() => handleDelete(purchase)}
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                              title="Fshi"
                            >
                              <Trash2 size={17} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-16 text-center text-gray-500"
                    >
                      <ShoppingBag
                        size={44}
                        className="mx-auto mb-3 text-gray-300"
                      />
                      Nuk ka blerje të regjistruara.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

     <div className="space-y-3 md:hidden">
  {filteredPurchases.map((purchase) => {
    const profit =
      Number(purchase.sale_price || 0) -
      Number(purchase.purchase_price || 0);

    return (
      <div
        key={purchase.id}
        tabIndex={0}
        onClick={(event) => {
          if (event.target.closest("button, a, input, select, textarea")) return;
          setDetailPurchase(purchase);
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setDetailPurchase(purchase);
          }
        }}
        className="w-full cursor-pointer rounded-2xl border border-gray-200 bg-white p-4 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {purchase.brand} {purchase.model}
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              {purchase.supplier_name ||
                `${purchase.customer_name || ""} ${
                  purchase.customer_surname || ""
                }`.trim() ||
                "—"}
            </p>
          </div>

          <PackagePlus size={22} className="text-blue-600" />
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-gray-100 pt-4">
          <div>
            <span className="text-xs text-gray-400">Blerja</span>
            <strong className="block text-sm">
              {formatMoney(purchase.purchase_price)}
            </strong>
          </div>

          <div>
            <span className="text-xs text-gray-400">Shitja</span>
            <strong className="block text-sm text-blue-600">
              {formatMoney(purchase.sale_price)}
            </strong>
          </div>

          <div>
            <span className="text-xs text-gray-400">Fitimi</span>
            <strong
              className={`block text-sm ${
                profit >= 0 ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {formatMoney(profit)}
            </strong>
          </div>
        </div>

        {/* VEPRIMET MOBILE */}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => setDetailPurchase(purchase)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-50 px-3 py-3 font-semibold text-blue-600"
          >
            <Eye size={18} />
            Shiko
          </button>

          <button type="button" onClick={() => openEditPurchase(purchase)}
            disabled={saving || deletingId === purchase.id}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-50 px-3 py-3 font-semibold text-amber-700 disabled:opacity-50">
            <Pencil size={18} /> Ndrysho
          </button>

          <button
            type="button"
            onClick={() => openPurchaseQr(purchase)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-violet-50 px-3 py-3 font-semibold text-violet-600"
          >
            <QrCode size={18} />
            QR
          </button>

          <button
            type="button"
            disabled={deletingId === purchase.id}
            onClick={() => handleDelete(purchase)}
            className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-red-50 text-red-600 disabled:opacity-50"
            aria-label="Fshi"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    );
  })}
</div>


       {qrPurchase && qrValue && (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-violet-600">
                  QR Pajisjeje
                </div>
                <h2 className="mt-1 text-xl font-black text-gray-900">
                  QR u gjenerua
                </h2>
              </div>
              <button
                type="button"
                onClick={closePurchaseQr}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                aria-label="Mbyll"
              >
                <X size={20} />
              </button>
            </div>

            <div
              ref={qrPrintRef}
              className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-6"
            >
              <QRCodeCanvas value={qrValue} size={240} level="H" includeMargin />
            </div>

            <button
              type="button"
              onClick={printPurchaseQr}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-3 font-bold text-white shadow-md transition hover:bg-violet-700"
            >
              <Printer size={19} />
              Printo QR
            </button>
          </div>
        </div>
      )}


        {/* QR SCANNER MODAL */}
{qrScannerOpen && (
  <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/80 p-3">
    <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">

      {/* HEADER */}
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-blue-600">
            Skanim QR
          </div>

          <h2 className="mt-1 text-xl font-black text-gray-900">
            Skano pajisjen
          </h2>
        </div>

        <button
          type="button"
          onClick={closeQrScanner}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600"
          aria-label="Mbyll"
        >
          <X size={20} />
        </button>
      </div>

      {/* CAMERA */}
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-black sm:aspect-square">

        <video
          ref={qrVideoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* Korniza e skanimit */}
        {!qrScannerError && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-56 w-56 rounded-3xl border-4 border-white/90 shadow-lg" />
          </div>
        )}

        {/* LOADING */}
        {qrScannerBusy && !qrScannerError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="rounded-xl bg-black/60 px-4 py-3 text-sm font-semibold text-white">
              Duke hapur kamerën...
            </div>
          </div>
        )}
      </div>

      {/* ERROR */}
      {qrScannerError && (
        <div className="p-5">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {qrScannerError}
          </div>

          <button
            type="button"
            onClick={() => {
              closeQrScanner();

              setTimeout(() => {
                startQrScanner();
              }, 300);
            }}
            className="mt-4 w-full rounded-xl bg-blue-600 px-4 py-3 font-bold text-white"
          >
            Provo përsëri
          </button>
        </div>
      )}

      {!qrScannerError && (
        <div className="p-4 text-center text-sm text-gray-500">
          Drejto kamerën te QR i pajisjes.
        </div>
      )}
    </div>
  </div>
)}


        {dialogOpen && (
          <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm">
            <div className="flex max-h-[94dvh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                    Blerje Pajisjeje
                  </span>

                  <h2 className="mt-1 text-xl font-black text-gray-900">
                    {editingPurchase ? "Ndrysho blerjen" : "Regjistro blerjen"}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={closeDialog}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <form
                data-purchase-form-scroll
                onSubmit={handleSubmit}
                className="min-h-0 flex-1 overflow-y-auto p-5"
              >
                {modalMessage.text && (
                  <div
                    className={`mb-5 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                      modalMessage.type === "success"
                        ? "border-green-200 bg-green-50 text-green-700"
                        : "border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-black ${
                          modalMessage.type === "success"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {modalMessage.type === "success" ? "✓" : "!"}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="font-bold">
                          {modalMessage.type === "success"
                            ? "Sukses"
                            : "Nuk u ruajt"}
                        </div>

                        <div className="mt-0.5 break-words font-medium">
                          {modalMessage.text}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setModalMessage({ type: "", text: "" })
                        }
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-lg hover:bg-black/5"
                        aria-label="Mbyll mesazhin"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}

                <FormSection title="Burimi i blerjes">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-semibold text-gray-700">
                        Blerë nga
                      </label>
                      <select
                        name="sourceType"
                        disabled={Boolean(editingPurchase)}
                        value={formData.sourceType}
                        onChange={(event) =>
                          setFormData((previous) => ({
                            ...previous,
                            sourceType: event.target.value,
                            supplierId: "",
                            customerName: "",
                            customerSurname: "",
                            customerPhone: "",
                            customerIdCard: "",
                          }))
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="customer">Klient</option>
                        <option value="supplier">Furnitor</option>
                      </select>
                    </div>

                    <InputField
                      label="Data e blerjes"
                      name="purchaseDate"
                      readOnly={Boolean(editingPurchase && formData.sourceType === "customer")}
                      type="date"
                      value={formData.purchaseDate}
                      onChange={handleChange}
                    />

                    {formData.sourceType === "supplier" && (
                      <>
                        <div>
                          <label className="mb-1 block text-sm font-semibold text-gray-700">
                            Furnitori *
                          </label>
                          <select
                            name="supplierId"
                        disabled={Boolean(editingPurchase)}
                            value={formData.supplierId}
                            onChange={handleChange}
                            className={`w-full rounded-xl border bg-white px-4 py-3 outline-none ${
                              fieldErrors.supplierId
                                ? "border-red-400"
                                : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            }`}
                          >
                            <option value="">Zgjidh furnitorin</option>
                            {suppliers.map((supplier) => (
                              <option key={supplier.id} value={supplier.id}>
                                {supplier.name}
                              </option>
                            ))}
                          </select>
                          {fieldErrors.supplierId && (
                            <p className="mt-1 text-xs font-medium text-red-600">
                              {fieldErrors.supplierId}
                            </p>
                          )}
                        </div>

                        <InputField
                          label="Numri i faturës"
                          name="invoiceNumber"
                          value={formData.invoiceNumber}
                          onChange={handleChange}
                          placeholder="Opsionale"
                        />
                      </>
                    )}
                  </div>
                </FormSection>

                {formData.sourceType === "customer" && (
                <FormSection title="Të dhënat e klientit">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="relative">
                      <label htmlFor="purchase-customer-name" className="mb-1 block text-sm font-semibold text-gray-700">
                        Emër *
                      </label>
                      <input
                        id="purchase-customer-name"
                        type="text"
                        name="customerName"
                        autoComplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={showCustomerSuggestions && Boolean(formData.customerName.trim())}
                        aria-controls="purchase-customer-suggestions"
                        value={formData.customerName}
                        onChange={handleChange}
                        onFocus={() => {
                          if (!selectedCustomerKey) setShowCustomerSuggestions(true);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") setShowCustomerSuggestions(false);
                          if (event.key === "Enter" && showCustomerSuggestions && filteredCustomers.length) {
                            event.preventDefault();
                            selectCustomer(filteredCustomers[0]);
                          }
                        }}
                        placeholder="Shkruaj emrin e klientit..."
                        className={`w-full rounded-xl border bg-white px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${
                          fieldErrors.customerName ? "border-red-400" : "border-gray-300"
                        }`}
                      />
                      {fieldErrors.customerName && (
                        <p className="mt-1 text-xs font-medium text-red-600">{fieldErrors.customerName}</p>
                      )}
                      {showCustomerSuggestions && formData.customerName.trim() && (
                        <div
                          id="purchase-customer-suggestions"
                          role="listbox"
                          className="absolute left-0 right-0 top-full z-[100] mt-1 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl"
                        >
                          {customerLoadError ? (
                            <p className="p-3 text-sm text-red-600">{customerLoadError}</p>
                          ) : filteredCustomers.length ? (
                            filteredCustomers.map((customer) => (
                              <button
                                key={customer.key}
                                type="button"
                                role="option"
                                aria-selected={false}
                                onClick={() => selectCustomer(customer)}
                                className="block w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50"
                              >
                                <span className="block font-semibold text-gray-800">
                                  {customer.name} {customer.surname}
                                </span>
                                <span className="block text-xs text-gray-500">{customer.phone || "Pa telefon"}</span>
                              </button>
                            ))
                          ) : (
                            <p className="p-3 text-sm text-gray-500">
                              Nuk u gjet klient. Plotëso fushat për klientin e ri.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <InputField
                      label="Mbiemër *"
                      name="customerSurname"
                      value={formData.customerSurname}
                      onChange={handleChange}
                      error={fieldErrors.customerSurname}
                    />

                    <InputField
                      label="Nr. telefoni *"
                      name="customerPhone"
                      type="tel"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      error={fieldErrors.customerPhone}
                    />

                    <InputField
                      label="Nr. Kartës ID *"
                      name="customerIdCard"
                      value={formData.customerIdCard}
                      onChange={handleChange}
                      error={fieldErrors.customerIdCard}
                    />
                  </div>
                </FormSection>
                )}

                <FormSection title="Të dhënat e pajisjes">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <SearchableDropdown label="Kategoria *" name="category"
                      value={formData.category} onChange={handleChange}
                      options={catalogOptionsFor("category", [], CATEGORY_OPTIONS)}
                      onAdd={(value) => addCatalogOption("category", [], value)}
                      error={fieldErrors.category} placeholder="Zgjidh ose shto kategori" />

                    <SearchableDropdown label="Brand *" name="brand"
                      value={formData.brand} onChange={handleChange}
                      options={catalogBrands} error={fieldErrors.brand}
                      onAdd={(value) => addCatalogOption("brand", [formData.category], value)}
                      placeholder="Kërko ose zgjidh brand-in" />

                    <SearchableDropdown label="Model *" name="model"
                      value={formData.model} onChange={handleChange}
                      options={catalogOptionsFor("model", [formData.category, formData.brand], catalogModels.map((item) => item.model))}
                      onAdd={(value) => addCatalogOption("model", [formData.category, formData.brand], value)}
                      error={fieldErrors.model}
                      placeholder="Kërko ose zgjidh modelin" />

                    <SearchableDropdown label="Nënkategoria" name="subcategory"
                      value={formData.subcategory} onChange={handleChange}
                      options={catalogOptionsFor("subcategory", [formData.category])}
                      onAdd={(value) => addCatalogOption("subcategory", [formData.category], value)}
                      placeholder="Zgjidh ose shto nënkategori" />

                    {[
                      { name: "storage", label: "Storage", placeholder: "Kërko memorien" },
                      { name: "ram", label: "RAM", placeholder: "Kërko RAM-in" },
                      { name: "color", label: "Ngjyra", placeholder: "Kërko ngjyrën" },
                    ].map((field) => (
                      <SearchableDropdown key={field.name} label={field.label}
                        name={field.name} value={formData[field.name]}
                        onChange={handleChange} options={catalogOptions[field.name]}
                        onAdd={(value) => addCatalogOption(field.name, [formData.category, formData.brand, formData.model], value)}
                        placeholder={field.placeholder} />
                    ))}


                    <InputField
                      label="IMEI"
                      name="imei"
                      value={formData.imei}
                      onChange={handleChange}
                    />

                    <InputField
                      label="Serial Number"
                      name="serialNumber"
                      value={formData.serialNumber}
                      onChange={handleChange}
                    />

                    <SelectField
                      label="Gjendja"
                      name="condition"
                      value={formData.condition}
                      onChange={handleChange}
                      options={CONDITION_OPTIONS}
                    />

                    <InputField
                      label="Bateria %"
                      name="batteryPercentage"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={formData.batteryPercentage}
                      onChange={handleChange}
                      error={fieldErrors.batteryPercentage}
                      placeholder="p.sh. 87"
                    />

                    <SelectField
                      label="Të servisuara"
                      name="serviced"
                      value={formData.serviced}
                      onChange={handleChange}
                      options={YES_NO_OPTIONS}
                    />

                    {formData.serviced === "Po" && (
                      <SelectField
                        label="Pjesa e servisuar *"
                        name="servicedParts"
                        value={formData.servicedParts[0] || ""}
                        onChange={(event) =>
                          setFormData((previous) => ({
                            ...previous,
                            servicedParts: event.target.value
                              ? [event.target.value]
                              : [],
                          }))
                        }
                        options={["", ...SERVICED_PART_OPTIONS]}
                        error={fieldErrors.servicedParts}
                      />
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <TextAreaField
                      label="Përshkrimi"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                    />

                    <TextAreaField
                      label="Defektet / dëmtimet"
                      name="defects"
                      value={formData.defects}
                      onChange={handleChange}
                    />
                  </div>
                </FormSection>

                <FormSection title="Foto dhe video">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="cursor-pointer rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/50 p-5 text-center transition hover:bg-blue-50">
                      <ImagePlus
                        size={30}
                        className="mx-auto text-blue-600"
                      />

                      <span className="mt-2 block font-semibold text-blue-700">
                        Shto foto
                      </span>

                      <span className="mt-1 block text-xs text-gray-500">
                        Mund të zgjedhësh disa foto.
                      </span>

                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImages}
                        className="hidden"
                      />
                    </label>

                    <label className="cursor-pointer rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/50 p-5 text-center transition hover:bg-violet-50">
                      <Video
                        size={30}
                        className="mx-auto text-violet-600"
                      />

                      <span className="mt-2 block font-semibold text-violet-700">
                        Shto video
                      </span>

                      <span className="mt-1 block text-xs text-gray-500">
                        Mund të zgjedhësh disa video.
                      </span>

                      <input
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={handleVideos}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {!!formData.images.length && (
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                      {formData.images.map((item, index) => (
                        <div
                          key={`${item.preview}-${index}`}
                          className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                        >
                          <img
                            src={item.preview}
                            alt={`Foto ${index + 1}`}
                            className="aspect-square w-full object-cover"
                          />

                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!!formData.videos.length && (
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {formData.videos.map((item, index) => (
                        <div
                          key={`${item.preview}-${index}`}
                          className="relative overflow-hidden rounded-xl border border-gray-200 bg-black"
                        >
                          <video
                            src={item.preview}
                            controls
                            className="aspect-video w-full object-contain"
                          />

                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </FormSection>

                <FormSection title="Çmimet dhe detyrimi">
                  <div className={`grid grid-cols-1 gap-4 ${formData.sourceType === "supplier" ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
                    <InputField
                      label="Çmimi i blerjes *"
                      name="purchasePrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.purchasePrice}
                      onChange={handleChange}
                      error={fieldErrors.purchasePrice}
                    />

                    <InputField
                      label="Çmimi i shitjes *"
                      name="salePrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.salePrice}
                      onChange={handleChange}
                      error={fieldErrors.salePrice}
                    />

                    {formData.sourceType === "supplier" && (
                      <InputField
                        label={editingPurchase ? "Shuma e paguar (ndryshohet te pagesat)" : "Shuma e paguar"}
                        name="amountPaid"
                        readOnly={Boolean(editingPurchase)}
                        type="number"
                        min="0"
                        max={formData.purchasePrice || undefined}
                        step="0.01"
                        value={formData.amountPaid}
                        onChange={handleChange}
                        error={fieldErrors.amountPaid}
                      />
                    )}

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <span className="block text-xs font-semibold uppercase text-emerald-600">
                        Fitimi potencial
                      </span>

                      <strong
                        className={`mt-1 block text-xl ${
                          potentialProfit >= 0
                            ? "text-emerald-700"
                            : "text-red-600"
                        }`}
                      >
                        {formatMoney(potentialProfit)} ALL
                      </strong>
                    </div>

                    {formData.sourceType === "supplier" && (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 md:col-span-4">
                        <span className="block text-xs font-semibold uppercase text-red-600">
                          Detyrimi që i shtohet furnitorit
                        </span>
                        <strong className="mt-1 block text-xl text-red-700">
                          {formatMoney(remainingDebt)} ALL
                        </strong>
                      </div>
                    )}
                  </div>
                </FormSection>

                <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={saving}
                    className="rounded-xl border border-gray-300 px-5 py-3 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anulo
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
                  >
                    <PackagePlus size={18} />
                    {saving ? "Duke ruajtur..." : editingPurchase ? "Ruaj ndryshimet" : "Ruaj Blerjen"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {detailPurchase && (
          <div
            className="fixed inset-0 z-[10010] flex items-end justify-center bg-black/55 backdrop-blur-sm sm:items-center sm:p-4"
            onClick={() => setDetailPurchase(null)}
          >
            <div
              className="dp-detail-dialog flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-3xl sm:rounded-3xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 bg-blue-700 px-5 py-5 text-white">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-blue-100">
                    Blerja #{detailPurchase.id}
                  </span>

                  <h2 className="mt-1 text-xl font-bold">
                    {detailPurchase.brand} {detailPurchase.model}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => setDetailPurchase(null)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DetailBox label="Blerë nga" value={detailPurchase.supplier_id ? "Furnitor" : "Klient"} />
                  <DetailBox label="Data e blerjes" value={String(detailPurchase.purchase_date || detailPurchase.created_at || "").slice(0, 10) || "—"} />
                  {!detailPurchase.supplier_id && (
                    <>
                      <DetailBox label="Emër" value={detailPurchase.customer_name} />
                      <DetailBox label="Mbiemër" value={detailPurchase.customer_surname} />
                    </>
                  )}
                  <DetailBox label="Kategoria" value={detailPurchase.category} />
                  <DetailBox label="Nënkategoria" value={detailPurchase.subcategory} />
                  <DetailBox label="Brand" value={detailPurchase.brand} />
                  <DetailBox label="Model" value={detailPurchase.model} />
                  <DetailBox label="Memoria" value={detailPurchase.storage} />
                  <DetailBox label="RAM" value={detailPurchase.ram} />
                  <DetailBox label="Ngjyra" value={detailPurchase.color} />
                  <DetailBox label="Gjendja" value={detailPurchase.device_condition} />

                  <DetailBox
                    label={detailPurchase.supplier_id ? "Furnitori" : "Klienti"}
                    value={
                      detailPurchase.supplier_name ||
                      `${detailPurchase.customer_name || ""} ${
                        detailPurchase.customer_surname || ""
                      }`.trim()
                    }
                  />

                  {detailPurchase.supplier_id && (
                    <>
                      <DetailBox
                        label="Shuma e paguar"
                        value={`${formatMoney(detailPurchase.amount_paid)} ALL`}
                      />
                      <DetailBox
                        label="Detyrimi"
                        value={`${formatMoney(
                          detailPurchase.amount_due ??
                            Math.max(
                              Number(detailPurchase.purchase_price || 0) -
                                Number(detailPurchase.amount_paid || 0),
                              0
                            )
                        )} ALL`}
                      />
                      <DetailBox
                        label="Numri i faturës"
                        value={detailPurchase.invoice_number}
                      />
                    </>
                  )}

                  <DetailBox
                    label="Telefoni"
                    value={detailPurchase.customer_phone}
                  />

                  <DetailBox
                    label="Kartë ID"
                    value={detailPurchase.customer_id_card}
                  />

                  <DetailBox
                    label="Pajisja"
                    value={`${detailPurchase.brand || ""} ${
                      detailPurchase.model || ""
                    }`.trim()}
                  />

                  <DetailBox
                    label="IMEI"
                    value={detailPurchase.imei}
                  />

                  <DetailBox
                    label="Serial Number"
                    value={detailPurchase.serial_number}
                  />

                  <DetailBox
                    label="Bateria"
                    value={
                      detailPurchase.battery_percentage !== null &&
                      detailPurchase.battery_percentage !== undefined &&
                      detailPurchase.battery_percentage !== ""
                        ? `${detailPurchase.battery_percentage}%`
                        : "—"
                    }
                  />

                  <DetailBox
                    label="Të servisuara"
                    value={detailPurchase.serviced || "Jo"}
                  />

                  {String(detailPurchase.serviced || "").toLowerCase() === "po" && (
                    <DetailBox
                      label="Pjesët e servisuara"
                      value={(() => {
                        const value = detailPurchase.serviced_parts;
                        if (Array.isArray(value)) return value.join(", ");
                        try {
                          const parsed = JSON.parse(value || "[]");
                          return Array.isArray(parsed) ? parsed.join(", ") : value;
                        } catch {
                          return value || "—";
                        }
                      })()}
                    />
                  )}

                  <DetailBox
                    label="Çmimi blerjes"
                    value={`${formatMoney(
                      detailPurchase.purchase_price
                    )} ALL`}
                  />

                  <DetailBox
                    label="Çmimi shitjes"
                    value={`${formatMoney(detailPurchase.sale_price)} ALL`}
                  />

                  <DetailBox
                    label="Product ID"
                    value={
                      detailPurchase.product_id
                        ? `#${detailPurchase.product_id}`
                        : "—"
                    }
                  />
                </div>

                <div className="mt-5 grid grid-cols-1 gap-3">
                  <DetailBox label="Përshkrimi" value={detailPurchase.description} />
                  <DetailBox label="Defektet" value={detailPurchase.defects} />
                </div>

                {normalizeUploads(detailPurchase.images).length > 0 && (
                  <div className="mt-5">
                    <h3 className="mb-3 font-bold text-gray-900">Foto</h3>

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {normalizeUploads(detailPurchase.images).map(
                        (image, index) => (
                          <img
                            key={`${image}-${index}`}
                            src={getMediaUrl(image)}
                            alt={`Foto ${index + 1}`}
                            className="aspect-square w-full rounded-xl border border-gray-200 object-cover"
                          />
                        )
                      )}
                    </div>
                  </div>
                )}

                {normalizeUploads(detailPurchase.videos).length > 0 && (
                  <div className="mt-5">
                    <h3 className="mb-3 font-bold text-gray-900">Video</h3>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {normalizeUploads(detailPurchase.videos).map(
                        (video, index) => (
                          <video
                            key={`${video}-${index}`}
                            src={getMediaUrl(video)}
                            controls
                            className="aspect-video w-full rounded-xl bg-black object-contain"
                          />
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="dp-detail-actions">
                <button
                  type="button"
                  onClick={() => setDetailPurchase(null)}
                  className="dp-detail-close"
                >
                  Mbyll
                </button>
                <button
                  type="button"
                  disabled={saving || deletingId === detailPurchase.id}
                  onClick={() => {
                    const purchase = detailPurchase;
                    setDetailPurchase(null);
                    openEditPurchase(purchase);
                  }}
                  className="dp-detail-edit"
                >
                  <Pencil size={17} />
                  Ndrysho
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const FormSection = ({ title, children }) => (
  <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-5">
    <h3 className="mb-4 text-base font-bold text-gray-900">{title}</h3>
    {children}
  </section>
);

// Searchable combobox with dropdown, keyboard navigation, and custom values.
const SearchableDropdown = ({ label, name, value, onChange, onAdd, options = [], error, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const normalized = String(value ?? "").toLocaleLowerCase("sq").trim();
  const available = [...new Set(options.filter(Boolean).map(String))];
  const filtered = available.filter((item) =>
    showAll || item.toLocaleLowerCase("sq").includes(normalized)
  );
  const canAdd = Boolean(onAdd && String(value || "").trim() &&
    !available.some((item) => item.toLocaleLowerCase("sq") === normalized));

  useEffect(() => {
    const closeOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, []);

  const update = (next) => onChange({ target: { name, value: next } });
  const choose = (next) => {
    update(next);
    setOpen(false);
    setShowAll(false);
    setActive(0);
    inputRef.current?.focus();
  };
  const handleKeyDown = (event) => {
    if (event.key === "Escape") { setOpen(false); return; }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) { setOpen(true); setActive(0); }
      else setActive((index) => Math.min(index + 1, filtered.length - 1));
    }
    if (event.key === "ArrowUp" && open) {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    }
    if (event.key === "Enter" && open && filtered.length) {
      event.preventDefault();
      choose(filtered[Math.max(0, active)]);
    }
  };

  return (
    <div className="relative min-w-0" ref={containerRef}>
      <label htmlFor={`purchase-combo-${name}`} className="mb-1 block text-sm font-semibold text-gray-700">{label}</label>
      <div className="relative">
        <input id={`purchase-combo-${name}`} ref={inputRef} name={name}
          value={value} autoComplete="off" placeholder={placeholder}
          role="combobox" aria-autocomplete="list" aria-expanded={open}
          aria-controls={`purchase-combo-list-${name}`}
          onFocus={() => { setOpen(true); setActive(0); }}
          onChange={(event) => { update(event.target.value); setShowAll(false); setOpen(true); setActive(0); }}
          onKeyDown={handleKeyDown}
          className={`w-full rounded-xl border bg-white py-3 pl-4 pr-11 outline-none focus:ring-2 focus:ring-blue-100 ${error ? "border-red-400" : "border-gray-300 focus:border-blue-500"}`}
        />
        <button type="button" aria-label={`Shfaq opsionet për ${label}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => { setShowAll(true); setOpen(true); setActive(0); inputRef.current?.focus(); }}
          className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
          <span aria-hidden="true">▾</span>
        </button>
      </div>
      {open && (
        <div id={`purchase-combo-list-${name}`} role="listbox"
          className="absolute left-0 right-0 top-full z-[200] mt-1 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
          {filtered.length ? filtered.map((option, index) => (
            <button key={option} type="button" role="option" aria-selected={option === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
              className={`block w-full px-4 py-2.5 text-left text-sm ${index === active ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"}`}>
              {option}
            </button>
          )) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              Nuk ka rezultate. Shkruaj një vlerë të re dhe kliko Shto.
            </div>
          )}
          {canAdd && (
            <button type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { onAdd(value); setOpen(false); }}
              className="sticky bottom-0 w-full border-t border-blue-100 bg-blue-50 px-4 py-3 text-left text-sm font-bold text-blue-700 hover:bg-blue-100">
              + Shto “{String(value).trim()}” në listë
            </button>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
};

const InputField = ({
  label,
  error,
  className = "",
  ...props
}) => (
  <div className={className}>
    <label className="mb-1 block text-sm font-semibold text-gray-700">
      {label}
    </label>

    <input
      {...props}
      className={`w-full rounded-xl border px-4 py-3 outline-none transition ${
        error
          ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
          : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      }`}
    />

    {error && (
      <p className="mt-1 text-xs font-medium text-red-600">{error}</p>
    )}
  </div>
);

const SelectField = ({
  label,
  name,
  value,
  onChange,
  options,
  error,
}) => (
  <div>
    <label className="mb-1 block text-sm font-semibold text-gray-700">
      {label}
    </label>

    <select
      name={name}
      value={value}
      onChange={onChange}
      className={`w-full rounded-xl border bg-white px-4 py-3 outline-none ${
        error
          ? "border-red-400"
          : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      }`}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>

    {error && (
      <p className="mt-1 text-xs font-medium text-red-600">{error}</p>
    )}
  </div>
);

const TextAreaField = ({
  label,
  name,
  value,
  onChange,
}) => (
  <div>
    <label className="mb-1 block text-sm font-semibold text-gray-700">
      {label}
    </label>

    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={4}
      className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    />
  </div>
);

const DetailBox = ({ label, value }) => (
  <div className="rounded-xl bg-gray-50 p-3">
    <span className="block text-xs font-medium text-gray-400">{label}</span>
    <strong className="mt-1 block break-words text-sm text-gray-800">
      {value || "—"}
    </strong>
  </div>
);

export default AdminDevicePurchases;
const devicePurchasesDesign = `
/* Blerje pajisjesh — vetëm pamja; elementet dhe funksionet ekzistuese ruhen. */
.dp-design{background:#f9fafb;color:#111827;font-family:inherit}.dp-content{font-size:14px;line-height:1.5;color:#374151}.dp-content button,.dp-content input,.dp-content select,.dp-content textarea{font-family:inherit;font-size:14px;line-height:1.5;letter-spacing:normal}.dp-content .font-black,.dp-content .font-extrabold{font-weight:700}.dp-content .font-bold{font-weight:600}
.dp-title{padding:0;border:0;background:transparent;box-shadow:none}.dp-title h1{font-size:30px;font-weight:700;line-height:1.25;letter-spacing:normal;color:#111827}.dp-title p{font-size:14px;font-weight:400;color:#6b7280}.dp-title span{font-size:12px;font-weight:600}.dp-title button{border-radius:12px;box-shadow:none;padding:12px 20px;font-weight:600}.dp-title button.bg-blue-600{background:#2563eb}.dp-title button.bg-blue-600:hover{background:#1d4ed8}
.dp-search-panel{padding:20px 24px;border-color:#e5e7eb;border-radius:16px;box-shadow:none}.dp-search-panel input{border-color:#d1d5db;border-radius:10px;font-size:14px;background:#fff;min-height:46px;color:#374151}
.dp-table-panel{border:1px solid #e5e7eb;border-radius:16px;box-shadow:none}.dp-table-panel table{font-size:14px;line-height:1.5}.dp-table-panel thead{background:#f9fafb;color:#6b7280;border-bottom:1px solid #e5e7eb}.dp-table-panel th{padding:16px 20px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.4px}.dp-table-panel td{padding:18px 20px;font-size:14px;color:#374151;border-color:#f3f4f6}.dp-table-panel tbody tr:hover{background:#f9fafb}.dp-table-panel td .font-bold,.dp-table-panel td .font-semibold{font-size:14px;font-weight:600;color:#111827}.dp-table-panel td .text-xs{font-size:12px;color:#9ca3af}.dp-table-panel td.font-mono{font-family:inherit;font-size:13px;letter-spacing:normal}.dp-table-panel td.text-blue-600{color:#2563eb}.dp-table-panel td.text-emerald-600{color:#059669}.dp-table-panel td.text-red-600{color:#dc2626}.dp-table-panel td.font-bold,.dp-table-panel td.font-extrabold{font-weight:600}.dp-table-panel td button{border:1px solid #e5e7eb;border-radius:8px;width:36px;height:36px}
.dp-content .shadow-sm{box-shadow:none}.dp-content .rounded-2xl{border-radius:16px}.dp-content .text-xs{font-size:12px}.dp-content label{font-size:14px;font-weight:500}.dp-content input,.dp-content select,.dp-content textarea{border-color:#d1d5db;border-radius:10px}.dp-content input:focus,.dp-content select:focus,.dp-content textarea:focus{outline:2px solid #bfdbfe;outline-offset:1px}.dp-content h2{font-size:22px;font-weight:700;letter-spacing:normal}.dp-content h3{font-weight:600}.dp-content .bg-blue-700.px-5.py-5{background:#f9fafb;color:#111827}.dp-content .bg-blue-700.px-5.py-5 .text-blue-100{color:#6b7280}.dp-content .bg-blue-700.px-5.py-5 button{color:#6b7280;background:#fff;border:1px solid #e5e7eb}.dp-content .rounded-3xl{border-radius:16px}.dp-content .rounded-t-3xl{border-top-left-radius:16px;border-top-right-radius:16px}
@media(max-width:767px){.dp-title h1{font-size:26px}.dp-title p{font-size:13px}.dp-search-panel{padding:16px}.dp-content .md\:hidden h3{font-size:16px;font-weight:600}.dp-content .md\:hidden .shadow-sm{box-shadow:none}.dp-content h2{font-size:20px}}
`;
const purchaseDetailStyles = `

/* Detajet e blerjes: të gjitha fushat e formularit, vetëm për lexim. */
.dp-detail-dialog {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.dp-detail-dialog strong {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.6;
}

.dp-detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
}

.dp-detail-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.dp-detail-actions .dp-detail-close {
  background: #f8fafc;
  color: #475569;
}

.dp-detail-actions .dp-detail-edit {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.dp-detail-actions button:disabled {
  opacity: .5;
  cursor: default;
}
`;